import * as THREE from "three";

// Two highlight strategies live here behind one switch. The diff-tween structure is
// identical for both; only how a part is marked "new" vs "prior" differs.
//
//  - 'opacity': new part fully opaque (1.0), prior parts dimmed (DIM_OPACITY).
//  - 'color'  : new part emissive-tinted, all parts near-opaque (fallback for when
//               many overlapping transparent parts read muddy — the 12-piston MA2 risk).
//
// Decided at the Phase 3 gate on real geometry. See plan.

export type HighlightMode = "opacity" | "color";

export const DIM_OPACITY = 0.8;

const BASE_COLOR = 0xd1d5db; // matches the Makistry seed default part color
const HIGHLIGHT_EMISSIVE = 0x2563eb; // blue tint for 'color' mode

/** The default part material (sRGB-authored → linear-correct under SRGBColorSpace). */
export function makeBaseMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(BASE_COLOR),
    roughness: 0.35,
    metalness: 0.1,
    transparent: true, // opacity is animated; always allow it
    opacity: 1,
  });
}

/**
 * Apply the visual "role" of a part for the current frame.
 * `opacity` is the tweened value (caller drives it); this only sets the static
 * styling that distinguishes new-vs-prior under the chosen mode.
 */
export function applyHighlight(
  mat: THREE.MeshStandardMaterial,
  opts: { isNew: boolean; mode: HighlightMode }
): void {
  if (opts.mode === "color") {
    // Prior parts stay opaque; the new part gets an emissive tint.
    mat.emissive.setHex(opts.isNew ? HIGHLIGHT_EMISSIVE : 0x000000);
    mat.emissiveIntensity = opts.isNew ? 0.35 : 0;
  } else {
    // Opacity mode: no emissive; contrast comes purely from the tweened opacity.
    mat.emissive.setHex(0x000000);
    mat.emissiveIntensity = 0;
  }
  mat.needsUpdate = true;
}

/**
 * Target opacity for a part given its role and the highlight mode.
 * In 'color' mode everything stays (near) opaque; in 'opacity' mode prior parts dim.
 */
export function targetOpacity(isNew: boolean, mode: HighlightMode): number {
  if (mode === "color") return 1;
  return isNew ? 1 : DIM_OPACITY;
}
